/**
 * A run's whole stock ledger as the platform summarises it (`GET /inseason/runs/{run_id}/stock-summary`,
 * bata-platform ADR 6073, bata-ui issue 319, `docs/adr/6021`). Pure: no React, no network.
 *
 * The Stock page used to walk `GET /inseason/stock-positions` to its end and sum it here. On the
 * 2026-10-07 runs that ledger is 53,720 and 290,440 rows, past the client's page rail, so the page
 * drew no figure at all. The platform now aggregates in the store, over EVERY position, and this
 * module only reshapes its answer: nothing is recomputed, and an absence stays an absence (the
 * summary's `null` sum, median and stock date are never nought).
 *
 * The cover beside each proposed line is the line's own `weeks_of_cover` (ADR 6073 B1), so ranking
 * by cover needs no ledger read either. A store-total line's is its article's at the store, every
 * size summed by the platform (ADR 6082, `docs/adr/6023`), and it is named so wherever it shows.
 */

import type { ProposalSummary, StockSummarySizeRun } from '@/api/generated';
import { ApiMalformedProblemError, ApiProblemError } from '@/api';
import { DISTINGUISHERS, type Series } from '@/charts';
import { sizeName, type CellValue } from '@/planning';

import type { SizeRunMap } from './size-run-map';

/** The refusal of a platform with no summary source wired (bata-platform ADR 6073). */
export const STOCK_SUMMARY_UNAVAILABLE = 'planning.inseason.stock_summary_unavailable';

/**
 * `?stockOut=counted` — the Stock page's table narrowed to the positions its «Counted out of stock» figure counts (ADR 9069): the
 * positions the stock summary counts as stock-outs, which the platform lists with `stock_out=counted` (ADR 6193). Pure.
 */
export function readStockOut(search: string): boolean {
  return new URLSearchParams(search).get('stockOut') === 'counted';
}

/**
 * Whether the summary will not come — the platform refused it as not wired, or the platform
 * running here predates the route (FastAPI's bare 404, or the skeleton's `platform.not_found`).
 * Either is a designed state of the page, never an error banner. A `planning.inseason.run_not_found`
 * is about THIS run, and is not this.
 */
export function isSummaryUnavailable(error: unknown): boolean {
  if (error instanceof ApiMalformedProblemError) return error.status === 404 || error.status === 501;
  if (!(error instanceof ApiProblemError)) return false;
  if (error.code === STOCK_SUMMARY_UNAVAILABLE || error.problem['planningCode'] === STOCK_SUMMARY_UNAVAILABLE) return true;
  if (error.status === 501) return true;
  return error.status === 404 && !error.code.startsWith('planning.');
}

/**
 * The size-run map from the summary's `size_run`: the run's axis in run order, the doors the
 * platform ranked by broken sizes (most first, ties by store), and how many doors there are. A size
 * the door has no row at is «not ranged»; a cell whose every row was unreported is «not reported» —
 * never a nought, which would draw a gap that was never counted.
 */
export function summarySizeRunMap(sizeRun: StockSummarySizeRun): SizeRunMap {
  const sizes = sizeRun.sizes.map((size) => sizeName(size));
  const doors: readonly Series[] = sizeRun.doors.map((door, index) => {
    const cells = new Map(door.sizes.map((cell) => [cell.size_key, cell.on_hand_units]));
    return {
      id: door.store_key,
      nameKey: 'inseason.series.doorNamed',
      nameValues: { door: door.store_key },
      distinguisher: DISTINGUISHERS[index % DISTINGUISHERS.length] ?? 'pattern-solid',
      data: sizes.map(({ key }) => {
        if (!cells.has(key)) return { missing: 'not-ranged' as const };
        const units = cells.get(key);
        return units === null || units === undefined ? { missing: 'not-reported' as const } : units;
      }),
    };
  });
  return { sizes, doors, totalDoors: sizeRun.doors_total };
}

/** The catalogue key the cover cell reads when the line carries no cover. */
export const COVER_ABSENT_KEY = {
  /** The ledger has no row at the line's grain (a run older than the stored positions). */
  noPosition: 'inseason.allocationStock.cover.noPosition',
  /** The position was measured, but its demand is nought: cover is undefined, never infinite and never nought. */
  noDemand: 'inseason.allocationStock.cover.noDemand',
  /**
   * A store-total line whose article total at the store has no cover: a size not reported or with
   * no rate, or a summed rate of nought (bata-platform ADR 6082). Not «no stock row»: the sizes are
   * in the ledger, and the total is withheld because it is not whole.
   */
  storeTotalUnmeasured: 'inseason.allocationStock.cover.storeTotalUnmeasured',
} as const;

/**
 * How a store-total line is named where a size would be (`docs/adr/6023`): `short` in a size
 * column's header, `label` in a line's own label, where it says whose figure the cover is.
 */
export const STORE_TOTAL_KEY = {
  short: 'inseason.position.storeTotal.short',
  label: 'inseason.position.storeTotal.label',
} as const;

/**
 * Whether the line's position is its article's total at the store, every size summed — a line at
 * the store-total size, whose grain the per-size ledger never holds (bata-platform issue 656, ADR
 * 6082). A platform older than the marker publishes none, and its lines read as they always did.
 */
export function isStoreTotalPosition(line: Pick<ProposalSummary, 'position_grain'>): boolean {
  return line.position_grain === 'store_total';
}

/**
 * The cover beside one proposed line, from the line itself (ADR 6073 B1). Five outcomes, none of
 * them nought weeks — which would be a door about to run dry:
 *
 *   • a measured cover — at a store-total line, the article's at the store (ADR 6082);
 *   • a store-total line whose total is not whole, or has no rate — said in words;
 *   • the position was not reported (`not-reported`);
 *   • the door does not carry the size (`not-ranged`);
 *   • the ledger has no row at the line's grain, or the position's demand is nought — said in words.
 */
export function lineCover(
  line: Pick<ProposalSummary, 'weeks_of_cover' | 'position_state' | 'position_grain'>, words: (key: string) => string,
): CellValue {
  const cover = line.weeks_of_cover ?? null;
  if (cover !== null) return { kind: 'number', value: cover };
  if (isStoreTotalPosition(line)) return { kind: 'text', value: words(COVER_ABSENT_KEY.storeTotalUnmeasured) };
  const state = line.position_state ?? null;
  if (state === null) return { kind: 'text', value: words(COVER_ABSENT_KEY.noPosition) };
  if (state === 'not_reported') return { kind: 'not-reported' };
  if (state === 'not_ranged') return { kind: 'not-ranged' };
  return { kind: 'text', value: words(COVER_ABSENT_KEY.noDemand) };
}
